export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** If set, the user must type this exact text to enable the confirm button. */
  requireTypedConfirmation?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  initialValue?: string;
  confirmLabel?: string;
  placeholder?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectDialogOptions {
  title: string;
  message?: string;
  options: SelectOption[];
  confirmLabel?: string;
}

/** "Choose exactly one of these known options" — a real `<select>`, not
 * promptDialog's free-text input mistyped against a list spelled out in a
 * message. Same modal shell as promptDialog/confirmDialog otherwise. */
export function selectDialog(options: SelectDialogOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    const modal = document.createElement('div');
    modal.className = 'modal';

    const title = document.createElement('h2');
    title.textContent = options.title;
    modal.appendChild(title);

    if (options.message) {
      const message = document.createElement('p');
      message.textContent = options.message;
      modal.appendChild(message);
    }

    const field = document.createElement('div');
    field.className = 'field';
    const select = document.createElement('select');
    select.style.width = '100%';
    for (const opt of options.options) {
      const optionEl = document.createElement('option');
      optionEl.value = opt.value;
      optionEl.textContent = opt.label;
      select.appendChild(optionEl);
    }
    field.appendChild(select);
    modal.appendChild(field);

    const actions = document.createElement('div');
    actions.className = 'row';
    actions.style.justifyContent = 'flex-end';
    actions.style.marginTop = '16px';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn';
    cancelBtn.textContent = 'Cancel';

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary';
    confirmBtn.textContent = options.confirmLabel ?? 'OK';

    const cleanup = (result: string | null) => {
      backdrop.remove();
      resolve(result);
    };

    cancelBtn.addEventListener('click', () => cleanup(null));
    confirmBtn.addEventListener('click', () => cleanup(select.value));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) cleanup(null);
    });

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    modal.appendChild(actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    select.focus();
  });
}

/** In-app text prompt, styled to match the rest of the UI (avoids native browser prompt() dialogs). */
export function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    const modal = document.createElement('div');
    modal.className = 'modal';

    const title = document.createElement('h2');
    title.textContent = options.title;
    modal.appendChild(title);

    if (options.message) {
      const message = document.createElement('p');
      message.textContent = options.message;
      modal.appendChild(message);
    }

    const field = document.createElement('div');
    field.className = 'field';
    const input = document.createElement('input');
    input.type = 'text';
    input.style.width = '100%';
    input.value = options.initialValue ?? '';
    if (options.placeholder) input.placeholder = options.placeholder;
    field.appendChild(input);
    modal.appendChild(field);

    const actions = document.createElement('div');
    actions.className = 'row';
    actions.style.justifyContent = 'flex-end';
    actions.style.marginTop = '16px';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn';
    cancelBtn.textContent = 'Cancel';

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary';
    confirmBtn.textContent = options.confirmLabel ?? 'OK';

    const cleanup = (result: string | null) => {
      backdrop.remove();
      resolve(result);
    };

    cancelBtn.addEventListener('click', () => cleanup(null));
    confirmBtn.addEventListener('click', () => cleanup(input.value.trim() || null));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') cleanup(input.value.trim() || null);
      if (e.key === 'Escape') cleanup(null);
    });
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) cleanup(null);
    });

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    modal.appendChild(actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    input.focus();
    input.select();
  });
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    const modal = document.createElement('div');
    modal.className = 'modal';

    const title = document.createElement('h2');
    title.textContent = options.title;

    const message = document.createElement('p');
    message.textContent = options.message;

    modal.appendChild(title);
    modal.appendChild(message);

    let typedInput: HTMLInputElement | null = null;
    if (options.requireTypedConfirmation) {
      const field = document.createElement('div');
      field.className = 'field';
      const label = document.createElement('label');
      label.textContent = `Type "${options.requireTypedConfirmation}" to confirm`;
      typedInput = document.createElement('input');
      typedInput.type = 'text';
      typedInput.style.width = '100%';
      field.appendChild(label);
      field.appendChild(typedInput);
      modal.appendChild(field);
    }

    const actions = document.createElement('div');
    actions.className = 'row';
    actions.style.justifyContent = 'flex-end';
    actions.style.marginTop = '16px';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn';
    cancelBtn.textContent = options.cancelLabel ?? 'Cancel';

    const confirmBtn = document.createElement('button');
    confirmBtn.className = options.danger ? 'btn btn-danger' : 'btn btn-primary';
    confirmBtn.textContent = options.confirmLabel ?? 'Confirm';

    if (typedInput) {
      confirmBtn.disabled = true;
      typedInput.addEventListener('input', () => {
        confirmBtn.disabled = typedInput!.value !== options.requireTypedConfirmation;
      });
    }

    const cleanup = (result: boolean) => {
      backdrop.remove();
      resolve(result);
    };

    cancelBtn.addEventListener('click', () => cleanup(false));
    confirmBtn.addEventListener('click', () => cleanup(true));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) cleanup(false);
    });

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    modal.appendChild(actions);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    typedInput?.focus();
  });
}
